import axios from "axios";

/**
 * iPASS Money 授權扣款。
 *
 * 流程 C：客戶在結帳頁選 iPASS Money → 呼叫 A1 取得授權頁網址 → 導過去 →
 * 客戶在 iPASS MONEY APP 同意授權 → 導回 confirmUrl → 換取 regKey。
 *
 * 身分：authorize／authorization／unbind 帶客戶 JWT，後端從 JWT 取 customer_id 歸戶，
 * 前端不再自己送 customer_key。confirm 只帶 TransactionId——後端在 A1 時就記住了
 * 這筆授權是誰發動的，導回頁面不需要 JWT 也能歸戶（真環境從一卡通 APP 導回時
 * 未必還在同一個 webview）。
 * 這幾支打 app-api（不是 dapp-api），本機測試可用 REACT_APP_IPASS_API_URL 覆蓋。
 */
// app-api 有全域前綴 /app-api（bootstrap.ts setGlobalPrefix）。預設跟其他 app-api 呼叫一樣走
// REACT_APP_API；本機要直連 app-api 時再用 REACT_APP_IPASS_API_URL 覆蓋。
const baseURL = process.env.REACT_APP_IPASS_API_URL || `${process.env.REACT_APP_API ?? ""}/app-api`;

const ipass = axios.create({
  baseURL,
  timeout: 15000,
  headers: { "Content-Type": "application/json" },
});

const authHeader = (jwtToken: string) => ({ Authorization: `Bearer ${jwtToken}` });

/** 授權完成後要導回的頁面（帶訂單號，回來才知道是哪一張；returnTo＝完成後要回去繼續的頁） */
export function ipassConfirmUrl(orderId: string, returnTo?: string): string {
  const u = new URL(`${window.location.origin}/ipass/confirm`);
  u.searchParams.set("orderId", orderId);
  if (returnTo) u.searchParams.set("returnTo", returnTo);
  return u.toString();
}

export function ipassCancelUrl(orderId: string): string {
  const u = new URL(`${window.location.origin}/ipass/cancel`);
  u.searchParams.set("orderId", orderId);
  return u.toString();
}

/**
 * 發動授權：拿到 paymentUrl 後直接導過去。
 *
 * 真環境的 paymentUrl 會喚醒 iPASS MONEY APP；mock 會導回我們自己的
 * confirmUrl 並補上 TransactionId，所以本機不必裝 iPASS APP 也走得完。
 */
export async function startIPassAuthorization(params: {
  orderId: string;
  amount: number;
  branchName?: string;
  /** 客戶 JWT；後端據此決定這筆授權歸誰 */
  jwtToken: string;
  /** 授權完成後要回去的站內路徑（預設回購物車） */
  returnTo?: string;
}): Promise<void> {
  const { data } = await ipass.post<{ transaction_id: string; payment_url: string }>(
    "/payments/ipass/authorize",
    {
      order_id: params.orderId,
      amount: params.amount,
      confirm_url: ipassConfirmUrl(params.orderId, params.returnTo),
      cancel_url: ipassCancelUrl(params.orderId),
      branch_name: params.branchName,
    },
    { headers: authHeader(params.jwtToken) },
  );
  window.location.href = data.payment_url;
}

/**
 * 授權頁導回後：以 TransactionId 換 regKey（regKey 留在後端，不回前端）。
 * state 是後端在 A1 時簽進 confirmUrl 的「這次授權是誰」，一卡通導回時原樣帶回，照轉給後端。
 */
export async function completeIPassAuthorization(params: {
  transactionId: string;
  state: string;
}): Promise<{ authorized: boolean; message?: string }> {
  const { data } = await ipass.post<{
    authorized: boolean;
    message?: string;
  }>("/payments/ipass/confirm", { transaction_id: params.transactionId, state: params.state });
  return data;
}

/** 目前登入的客戶有沒有有效授權——有的話下單就不必再跳授權頁 */
export async function hasIPassAuthorization(jwtToken: string): Promise<boolean> {
  const { data } = await ipass.get<{ authorized: boolean }>("/payments/ipass/authorization", {
    headers: authHeader(jwtToken),
  });
  return data.authorized;
}

/** 客戶解除 iPASS 自動扣款授權 */
export async function unbindIPassAuthorization(jwtToken: string): Promise<void> {
  await ipass.post("/payments/ipass/unbind", {}, { headers: authHeader(jwtToken) });
}

/** 付款方式的識別值，與 cash_on_delivery / 信用卡 id 並列 */
export const IPASS_PAY_REF = "ipass_money";

/* ---------- 入口 A：從 iPASS MONEY APP 轉導進來 ---------- */

export interface IPassEntryProfile {
  name: string;
  phone: string;
  birthday: string | null;
  ep_account_last4: string | null;
}

export type IPassEntryResult =
  | { registered: boolean; sharing_req_id: string; profile: IPassEntryProfile; error?: undefined }
  | { registered: false; error: { rtn_code: string; message: string } };

/**
 * 把轉導連結上的 iPASSdata / IV 交給後端解析。身分只認 LINE access token（未註冊者也要能進來）。
 * 參數值請直接用 URLSearchParams 取到的（已 decode 一次），不要再 decode。
 */
export async function resolveIPassEntry(params: {
  iPASSdata: string;
  iv: string;
  lineAccessToken: string;
}): Promise<IPassEntryResult> {
  const { data } = await ipass.post<IPassEntryResult>(
    "/payments/ipass/entry",
    { iPASSdata: params.iPASSdata, IV: params.iv },
    { headers: { "X-Line-Access-Token": params.lineAccessToken } },
  );
  return data;
}
